<template>
  <div>
    <v-toolbar flat>
      <v-toolbar-title class="text-h5">
        {{ $t('entities.paymentMethods.title') }}
      </v-toolbar-title>
      <v-spacer />
      <RefreshButton :refresh="fetchPaymentMethods" :loading="isLoading" />
    </v-toolbar>
    <v-divider />
    <v-data-table
      :headers="headers"
      :items="paymentMethods"
      :items-per-page="-1"
      :loading="isLoading"
      :loading-text="$t('components.noData.fetchingEntries')"
      class="elevation-0"
      hide-default-footer>
      <template v-slot:item.attached="{ item }">
        <div class="d-flex justify-center">
          <v-progress-circular
            v-if="toggling[item.id]"
            indeterminate
            size="20"
            width="2"
            color="primary" />
          <v-simple-checkbox
            v-else
            :value="isAttached(item.id)"
            :disabled="!canManagePaymentMethods"
            color="primary"
            @click.stop="requestToggle(item)" />
        </div>
      </template>
      <template v-slot:item.code="{ item }">
        <span class="font-weight-bold">{{ item.code }}</span>
      </template>
      <template v-slot:item.l10n_name_key="{ item }">
        <span>{{ item.l10n_name_key || '—' }}</span>
      </template>
      <template v-slot:no-data>
        <div class="py-6 text-center grey--text">
          {{ $t('components.noData.noEntriesFound') }}
        </div>
      </template>
    </v-data-table>
    <ConfirmationDialog
      @dialog:confirm="confirmToggle"
      @dialog:close="closeConfirmationDialog"
      :is-being-added-to-arena="pendingAction?.shouldAttach"
      :is-opened="isConfirmationDialogOpen"
      :is-loading="isDialogSubmitting"
      :item-name="pendingAction?.item?.name" />
  </div>
</template>

<script>
import PaymentMethod from '@/models/shop/PaymentMethod'
import RefreshButton from '@/components/crud/RefreshButton.vue'
import isRole from '@/mixins/crud/computed/isRole'
import ConfirmationDialog from './ConfirmationDialog.vue'

export default {
  name: 'PaymentMethodsTab',
  components: {
    RefreshButton,
    ConfirmationDialog,
  },
  mixins: [isRole],
  data() {
    return {
      isLoading: false,
      paymentMethods: [],
      arenaPaymentMethodIds: [],
      toggling: {},
      pendingAction: null,
      isConfirmationDialogOpen: false,
      isDialogSubmitting: false,
    }
  },
  computed: {
    arenaId() {
      return this.$store.getters.arena ? this.$store.getters.arena.id : null
    },
    canManagePaymentMethods() {
      return this.isAdmin || this.isManager
    },
    headers() {
      const headers = [
        { text: this.$t('generalAttributes.name'), value: 'name' },
        {
          text: this.$t('entities.paymentMethods.attributes.code'),
          value: 'code',
        },
      ]
      if (this.isAdmin || this.isManager) {
        headers.push({
          text: this.$t('entities.paymentMethods.attributes.addedToArena'),
          value: 'attached',
          sortable: false,
          align: 'center',
          width: '150px',
          class: 'px-0',
          cellClass: 'px-0 text-center',
        })
      }
      return headers
    },
  },
  methods: {
    async fetchPaymentMethods() {
      if (!this.arenaId) {
        this.paymentMethods = []
        this.attachedPaymentMethodIds = []
        this.isLoading = false
        return
      }
      this.isLoading = true
      try {
        const apiCalls = [
          PaymentMethod.api.getArenaPaymentMethods(this.arenaId),
        ]
        if (this.isAdmin || this.isManager)
          apiCalls.unshift(PaymentMethod.api.list())
        const [allResponse, attachedResponse] = await Promise.all(apiCalls)
        const allEntries = this.unpackResponse(allResponse)
        const arenaEntries = this.unpackResponse(attachedResponse)
        this.paymentMethods = allEntries.map((it) => new PaymentMethod(it))
        this.arenaPaymentMethodIds = arenaEntries.map((it) => it.id)
      } catch (e) {
        console.error(e)
        this.paymentMethods = []
        this.arenaPaymentMethodIds = []
      } finally {
        this.isLoading = false
      }
    },
    closeConfirmationDialog() {
      this.isConfirmationDialogOpen = false
      this.pendingAction = null
      this.isDialogSubmitting = false
    },
    async confirmToggle() {
      if (!this.pendingAction || !this.arenaId) return
      const { item, shouldAttach } = this.pendingAction
      const itemId = item.id
      if (this.toggling[itemId]) return
      this.isDialogSubmitting = true
      this.$set(this.toggling, itemId, true)
      try {
        if (shouldAttach) {
          await PaymentMethod.api.addPaymentMethodToArena(this.arenaId, {
            payment_method_id: itemId,
          })
        } else {
          await PaymentMethod.api.removePaymentMethodFromArena(
            this.arenaId,
            itemId,
          )
        }
        this.updateAttachedIds(itemId, shouldAttach)
        this.closeConfirmationDialog()
      } catch (e) {
        console.error(e)
      } finally {
        this.isDialogSubmitting = false
        this.$delete(this.toggling, itemId)
      }
    },
    requestToggle(item) {
      if (!this.canManagePaymentMethods) return
      this.pendingAction = {
        item,
        shouldAttach: !this.isAttached(item.id),
      }
      this.isConfirmationDialogOpen = true
    },
    isAttached(id) {
      return this.arenaPaymentMethodIds.includes(id)
    },
    updateAttachedIds(id, attach) {
      if (attach) {
        if (!this.isAttached(id)) {
          this.arenaPaymentMethodIds = [...this.arenaPaymentMethodIds, id]
        }
      } else {
        this.arenaPaymentMethodIds = this.arenaPaymentMethodIds.filter(
          (existingId) => existingId !== id,
        )
      }
    },
    unpackResponse(response) {
      const payload = response?.data?.data ?? response?.data ?? []
      if (Array.isArray(payload)) {
        return payload
      }
      if (Array.isArray(payload?.data)) {
        return payload.data
      }
      return []
    },
  },
  watch: {
    arenaId: {
      immediate: true,
      handler() {
        return this.fetchPaymentMethods()
      },
    },
  },
}
</script>
